// src/pages/Tabs/OnStreetEnforcementPage.jsx
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  FileText,
  CheckCircle2,
  CreditCard,
  Percent,
  XCircle,
  Coins,
  Users,
  Download,
  CircleParking,
  Building2,
  Globe,
  SlidersHorizontal,
  CalendarRange,
  Calendar,
  Check,
  X,
  Loader2,
  TrendingUp,
  Activity,
  AlertTriangle,
} from "lucide-react";
import { apiCall } from "@/api/apiCall";
import ReactSelect from "@/components/common/ReactSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import NewChartKPI from "@/components/child/NewChartKPI";
import LineBar from "@/components/child/line_bars";
import StackedBarChart from "@/components/child/StackedBarChart";
import { OverviewShimmer } from "@/components/child/OnStreetEnforcementShimmer";
import StackedAreaPercentChart from "@/components/child/StackedAreaPercentChart";
import { usePermissions } from "@/context/PermissionContext";

// ============================================
// Constants - ONSTREET CONTRACTS with proper mapping
// ============================================
const ONSTREET_CONTRACTS = {
  name: "901",
  fullName: "Saudi Arabia",
  children: [
    { name: "0030021", fullName: "AL-BAHA" },
    { name: "0030006", fullName: "AL-HASSA" },
    { name: "0030018", fullName: "BALJURASHI" },
    { name: "1501005", fullName: "DIGITAL CITY" },
    { name: "0030001", fullName: "DAMMAM" },
    { name: "0020001", fullName: "JEDDAH" },
    { name: "1930002", fullName: "JEDDAH JV" },
    { name: "0030013", fullName: "JUBAIL" },
    { name: "0030017", fullName: "KFCA" },
    { name: "0030014", fullName: "KFMH" },
    { name: "0030009", fullName: "KHAMISH MUSHAITH" },
    { name: "0030001", fullName: "KHOBAR" },
    { name: "0040003", fullName: "MADINA" },
    { name: "1400101", fullName: "MAKKAH" },
    { name: "0020025", fullName: "MINISTRY OF CULTURE" },
    { name: "0020013", fullName: "TAIF" },
    { name: "2010001", fullName: "RIYADH PARKING" },
  ],
};

// ============================================
// UNIQUE KEY for each contract (code + name)
// ============================================
const getContractKey = (contract) => `${contract.name}|${contract.fullName}`;

const PARENT_CONTRACT_OPTION = {
  value: ONSTREET_CONTRACTS.name,
  label: ONSTREET_CONTRACTS.fullName,
};
const ALL_CONTRACT_OPTIONS = ONSTREET_CONTRACTS.children.map((c) => ({
  value: c.name,
  label: c.fullName,
  key: getContractKey(c),
}));

// ============================================
// API Permission to Contract Mapping - COMPLETE
// ============================================
const API_PERMISSION_TO_CONTRACT = {
  "BHA-AL-BAHA": { name: "0030021", fullName: "AL-BAHA" },
  "BLJ-BALJURASHI": { name: "0030018", fullName: "BALJURASHI" },
  "DAM-Dammam": { name: "0030001", fullName: "DAMMAM" },
  "DC-DIGITAL CITY": { name: "1501005", fullName: "DIGITAL CITY" },
  "HAS-AL-HASA": { name: "0030006", fullName: "AL-HASSA" },
  "JED-JEDDAH": { name: "0020001", fullName: "JEDDAH" },
  "JUB-JUBAIL": { name: "0030013", fullName: "JUBAIL" },
  "JV-JEDDAH JV": { name: "1930002", fullName: "JEDDAH JV" },
  "KFC-KFCA": { name: "0030017", fullName: "KFCA" },
  "KFMH-KFMH": { name: "0030014", fullName: "KFMH" },
  "KHA-KHAMIS MUSHAIT": { name: "0030009", fullName: "KHAMISH MUSHAITH" },
  "KHO-KHOBER": { name: "0030001", fullName: "KHOBAR" },
  "MAK-MAKKAH": { name: "1400101", fullName: "MAKKAH" },
  "MED-MADINAH": { name: "0040003", fullName: "MADINA" },
  "MOC-MINISTRY OF CULTURE": { name: "0020025", fullName: "MINISTRY OF CULTURE" },
  "RYDO-RIYADH PARKING": { name: "2010001", fullName: "RIYADH PARKING" },
  "Saudi Arabia": { name: "901", fullName: "Saudi Arabia" },
  "TAF-TAIF": { name: "0020013", fullName: "TAIF" },
};

// ============================================
// Helper Functions
// ============================================
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const ENFORCEMENT_COLUMNS = [
  "Total Action",
  "CNS",
  "Escalation",
  "TOW",
  "CLAMP",
  "Storage",
  "No Action (NACT)",
];
const REVENUE_COLORS = {
  CNS: "#1787B0",
  CLAMP: "#E17A35",
  TOW: "#FAFA01",
  STORAGE: "#1baf7a",
};
const REVENUE_FALLBACK_COLOR = "#94a3b8";
const colorForRevenueLabel = (label) =>
  REVENUE_COLORS[
    String(label || "")
      .trim()
      .toUpperCase()
  ] || REVENUE_FALLBACK_COLOR;

// ============================================
// NORMALIZE NAME - Complete Fix
// ============================================
const normalizeName = (s) => {
  if (!s) return "";

  let cleaned = String(s).toUpperCase().trim();

  if (cleaned.includes("-")) {
    cleaned = cleaned.split("-").slice(1).join("-");
  }

  cleaned = cleaned.replace(/\s+/g, " ").trim();

  const specialMappings = {
    "AL-HASA": "AL-HASSA",
    "KHAMIS MUSHAIT": "KHAMISH MUSHAITH",
    KHOBER: "KHOBAR",
    MADINAH: "MADINA",
  };

  return specialMappings[cleaned] || cleaned;
};

// ============================================
// Normalize API Permissions to Contracts
// ============================================
const normalizeToContract = (permission) => {
  if (!permission) return null;
  const trimmed = String(permission).trim();

  // Direct mapping from API string
  if (API_PERMISSION_TO_CONTRACT[trimmed]) {
    return API_PERMISSION_TO_CONTRACT[trimmed];
  }

  // Try to find by code
  const foundByCode = ONSTREET_CONTRACTS.children.find((c) => c.name === trimmed);
  if (foundByCode) return foundByCode;

  // Try normalized name matching
  const normalizedName = normalizeName(trimmed);
  const foundByName = ONSTREET_CONTRACTS.children.find(
    (c) => normalizeName(c.fullName) === normalizedName
  );
  if (foundByName) return foundByName;

  return null;
};

const normalizePermissions = (userContracts) => {
  if (!userContracts) return { onstreet: [], offstreet: [] };

  const normalizeList = (list) => {
    if (!list || !Array.isArray(list)) return [];
    return list.map((item) => normalizeToContract(item)).filter(Boolean);
  };

  return {
    onstreet: normalizeList(userContracts.onstreet),
    offstreet: normalizeList(userContracts.offstreet),
  };
};

// ============================================
// Other Helpers
// ============================================
const parseNum = (v) => {
  if (v === undefined || v === null || v === "" || v === "-") return null;
  const cleaned = String(v)
    .replace(/[(),%]/g, "")
    .trim();
  const n = Number(cleaned);
  return Number.isNaN(n) ? null : n;
};

const isNegative = (v) => {
  if (v === undefined || v === null || v === "" || v === "-") return false;
  if (typeof v === "number") return v < 0;
  const str = String(v).trim();
  if (str.startsWith("(") && str.endsWith(")")) return true;
  const n = parseNum(str);
  return n !== null && n < 0;
};

const buildMonthParam = (month) => {
  if (!month) return null;
  if (month.match(/^\d{2}-[A-Za-z]{3}$/)) return month;
  if (["Full_Year", "FY"].includes(month)) return month;
  return month;
};

const extractMonthAbbrev = (month) => {
  const match = String(month || "").match(/^\d{2}-([A-Za-z]{3})$/);
  return match ? match[1] : month;
};

const buildMonthlySeries = (
  issueCount,
  paidCount,
  paidRate,
  selectedMonth,
  selectedYear,
  isolateYear
) => {
  const isRangeSelection = ["Full_Year", "FY"].includes(selectedMonth);
  const selectedMonthIdx = isRangeSelection
    ? -1
    : MONTH_NAMES.indexOf(extractMonthAbbrev(selectedMonth));
  const allYears = Object.keys(issueCount).sort();
  const hasIsolateYear = isolateYear && isolateYear !== "All";
  const years = hasIsolateYear
    ? allYears.filter((y) => y === isolateYear)
    : selectedYear
      ? allYears.filter((y) => Number(y) <= Number(selectedYear))
      : allYears;

  const allMonthsSet = new Set();
  years.forEach((year) => {
    const yearData = issueCount[year] || {};
    const isSelectedYear = !hasIsolateYear && selectedYear && Number(year) === Number(selectedYear);
    Object.keys(yearData).forEach((month) => {
      const monthIdx = MONTH_NAMES.indexOf(month);
      const monthAllowed =
        !isSelectedYear ||
        isRangeSelection ||
        selectedMonthIdx === -1 ||
        monthIdx <= selectedMonthIdx;
      if (monthAllowed) allMonthsSet.add(`${year}|${month}`);
    });
  });

  const orderedPairs = Array.from(allMonthsSet)
    .map((key) => {
      const [year, month] = key.split("|");
      return { year, month };
    })
    .sort((a, b) =>
      a.year !== b.year
        ? Number(a.year) - Number(b.year)
        : MONTH_NAMES.indexOf(a.month) - MONTH_NAMES.indexOf(b.month)
    );

  return orderedPairs.map(({ year, month }) => {
    const issueVal = issueCount?.[year]?.[month];
    const paidVal = paidCount?.[year]?.[month];
    const rateVal = paidRate?.[year]?.[month];
    const revenue =
      issueVal !== undefined && issueVal !== null
        ? parseFloat(String(issueVal).replace(/,/g, ""))
        : null;

    return {
      month: `${month}-${year}`,
      year,
      revenue: revenue !== null ? revenue : 0,
      transaction:
        paidVal !== undefined && paidVal !== null
          ? parseFloat(String(paidVal).replace(/,/g, ""))
          : 0,
      occupancy:
        rateVal !== undefined && rateVal !== null
          ? parseFloat(String(rateVal).replace(/,/g, "").replace("%", ""))
          : 0,
      _raw: {
        revenue: issueVal !== undefined ? issueVal : null,
        paid: paidVal !== undefined ? paidVal : null,
        rate: rateVal !== undefined ? rateVal : null,
      },
    };
  });
};

const transformGraphData = (
  graphData,
  selectedMonth,
  selectedYear,
  dataSource = "cns",
  isolateYear
) => {
  const srcData = graphData?.[dataSource];
  if (!srcData) return [];
  return buildMonthlySeries(
    srcData.cn_issue_count || {},
    srcData.cn_paid_count || {},
    srcData.cn_paid_rate || {},
    selectedMonth,
    selectedYear,
    isolateYear
  );
};

const BREAKDOWN_KEY_CONFIG = {
  eo_paid_count: { label: "EO Paid", color: "#1787B0" },
  tow_paid_count: { label: "TOW Paid", color: "#FAFA01" },
  clamp_paid_count: { label: "Clamp Paid", color: "#E17A35" },
  nact_issue_count: { label: "NACT Issued", color: "#E5E5E5" },
};
const BREAKDOWN_PALETTE = ["#1787B0", "#FAFA01", "#E17A35", "#1baf7a", "#808080", "#f43f5e"];
const formatBreakdownKey = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
const breakdownLabelFor = (key) => BREAKDOWN_KEY_CONFIG[key]?.label || formatBreakdownKey(key);
const breakdownColorFor = (key, idx = 0) =>
  BREAKDOWN_KEY_CONFIG[key]?.color || BREAKDOWN_PALETTE[idx % BREAKDOWN_PALETTE.length];
const BREAKDOWN_LEFT_FORMATTER = (v) => v.toLocaleString();
const BREAKDOWN_RIGHT_FORMATTER = (v) => Math.round(v).toLocaleString();
const BREAKDOWN_Y_AXIS_FORMATTERS = {
  left: BREAKDOWN_LEFT_FORMATTER,
  right: BREAKDOWN_RIGHT_FORMATTER,
};

const buildBreakdownChart = (graphData, dataSource, selectedMonth, selectedYear) => {
  const breakdownObj = graphData?.[dataSource]?.breakdown || {};
  const keys = Object.keys(breakdownObj);
  if (keys.length === 0) return { months: [], yearRanges: [], datasets: [] };

  const isRangeSelection = ["Full_Year", "FY"].includes(selectedMonth);
  const selectedMonthIdx = isRangeSelection
    ? -1
    : MONTH_NAMES.indexOf(extractMonthAbbrev(selectedMonth));
  const allYears = Array.from(
    new Set(keys.flatMap((k) => Object.keys(breakdownObj[k] || {})))
  ).sort();
  const years = selectedYear ? allYears.filter((y) => Number(y) <= Number(selectedYear)) : allYears;

  const orderedPairs = [];
  years.forEach((year) => {
    const isSelectedYear = selectedYear && Number(year) === Number(selectedYear);
    MONTH_NAMES.forEach((month, idx) => {
      const hasAnyValue = keys.some((k) => breakdownObj[k]?.[year]?.[month] !== undefined);
      if (!hasAnyValue) return;
      const monthAllowed =
        !isSelectedYear || isRangeSelection || selectedMonthIdx === -1 || idx <= selectedMonthIdx;
      if (monthAllowed) orderedPairs.push({ year, month });
    });
  });

  const months = orderedPairs.map((p) => p.month);
  const yearRanges = [];
  orderedPairs.forEach(({ year }) => {
    const last = yearRanges[yearRanges.length - 1];
    if (last && last.year === year) last.span += 1;
    else yearRanges.push({ year, span: 1 });
  });

  const datasets = keys.map((key, idx) => ({
    label: breakdownLabelFor(key),
    color: breakdownColorFor(key, idx),
    data: orderedPairs.map(({ year, month }) => {
      const cell = breakdownObj[key]?.[year]?.[month];
      if (cell === undefined || cell === null) return null;
      const n = parseFloat(String(cell).replace(/,/g, ""));
      return Number.isNaN(n) ? null : n;
    }),
  }));

  return { months, yearRanges, datasets };
};

const transformBreakdownToKPI = (
  graphData,
  dataSource,
  selectedMonth,
  selectedYear,
  isolateYear
) => {
  const breakdownObj = graphData?.[dataSource]?.breakdown || {};
  const keys = Object.keys(breakdownObj);
  if (keys.length === 0) return [];
  const [k1, k2, k3] = keys;
  return buildMonthlySeries(
    breakdownObj[k1] || {},
    k2 ? breakdownObj[k2] || {} : {},
    k3 ? breakdownObj[k3] || {} : {},
    selectedMonth,
    selectedYear,
    isolateYear
  );
};

const buildRevenueSummaryChart = (
  revenueSummary,
  selectedMonth,
  selectedYear,
  isolateYear,
  valueKey = "value"
) => {
  if (!revenueSummary || revenueSummary.length === 0)
    return { months: [], yearRanges: [], datasets: [] };

  const isRangeSelection = ["Full_Year", "FY"].includes(selectedMonth);
  const selectedMonthIdx = isRangeSelection
    ? -1
    : MONTH_NAMES.indexOf(extractMonthAbbrev(selectedMonth));
  const allYears = Object.keys(revenueSummary[0]?.data || {}).sort();
  const hasIsolateYear = isolateYear && isolateYear !== "All";
  const years = hasIsolateYear
    ? allYears.filter((y) => y === isolateYear)
    : selectedYear
      ? allYears.filter((y) => Number(y) <= Number(selectedYear))
      : allYears;

  const orderedPairs = [];
  years.forEach((year) => {
    const isSelectedYear = !hasIsolateYear && selectedYear && Number(year) === Number(selectedYear);
    MONTH_NAMES.forEach((month, idx) => {
      const hasAnyValue = revenueSummary.some((entry) => entry.data?.[year]?.[month] !== undefined);
      if (!hasAnyValue) return;
      const monthAllowed =
        !isSelectedYear || isRangeSelection || selectedMonthIdx === -1 || idx <= selectedMonthIdx;
      if (monthAllowed) orderedPairs.push({ year, month });
    });
  });

  const months = orderedPairs.map((p) => p.month);
  const yearRanges = [];
  orderedPairs.forEach(({ year }) => {
    const last = yearRanges[yearRanges.length - 1];
    if (last && last.year === year) last.span += 1;
    else yearRanges.push({ year, span: 1 });
  });

  const datasets = revenueSummary.map((entry) => ({
    label: entry.label,
    color: colorForRevenueLabel(entry.label),
    data: orderedPairs.map(({ year, month }) => {
      const cell = entry.data?.[year]?.[month];
      if (!cell) return null;
      const raw = valueKey === "percentage" ? cell.percentage : cell.value;
      if (raw === undefined || raw === null) return null;
      const n = parseFloat(String(raw).replace(/[,%]/g, ""));
      return Number.isNaN(n) ? null : n;
    }),
    percentages: orderedPairs.map(({ year, month }) => {
      const raw = entry.data?.[year]?.[month]?.percentage;
      if (raw === undefined || raw === null) return null;
      const n = parseFloat(String(raw).replace(/[,%]/g, ""));
      return Number.isNaN(n) ? null : n;
    }),
  }));

  return { months, yearRanges, datasets };
};

// ============================================
// Components
// ============================================
const KpiCard = ({ icon: Icon, label, value, tone = "default" }) => {
  const toneMap = {
    default: "bg-primary/10 text-primary",
    success: "bg-emerald-500/10 text-emerald-500",
    danger: "bg-red-500/10 text-red-500",
    warning: "bg-amber-500/10 text-amber-500",
  };
  const negative = isNegative(value);
  return (
    <div className="rounded-xl border border-border bg-card p-3.5 flex items-center gap-3">
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${toneMap[tone]}`}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold truncate">
          {label}
        </p>
        <p
          className={`text-lg font-bold leading-tight tabular-nums ${negative ? "text-red-500" : "text-foreground"}`}
        >
          {value}
        </p>
      </div>
    </div>
  );
};

const DateFilterPanel = ({
  onApply,
  onReset,
  onClose,
  topPos,
  activeYear,
  initialGroup = "",
  initialMonth = "",
  initialDay = null,
  monthGroups = [],
  yearOptions = [],
  optLoading = false,
}) => {
  const [selYear, setSelYear] = useState({
    value: activeYear || String(new Date().getFullYear()),
    label: activeYear || String(new Date().getFullYear()),
  });
  const [selGroup, setSelGroup] = useState(initialGroup);
  const [selMonth, setSelMonth] = useState(initialMonth);
  const [selDay, setSelDay] = useState(
    initialDay || (initialMonth ? { value: initialMonth, label: initialMonth } : null)
  );
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const loading = optLoading;

  useEffect(() => {
    setSelYear({
      value: activeYear || String(new Date().getFullYear()),
      label: activeYear || String(new Date().getFullYear()),
    });
    setSelGroup(initialGroup);
    setSelMonth(initialMonth);
    setSelDay(initialDay || (initialMonth ? { value: initialMonth, label: initialMonth } : null));
  }, [activeYear, initialGroup, initialMonth, initialDay?.value]);

  useEffect(() => {
    if (!monthGroups || monthGroups.length === 0) return;
    if (initialGroup && initialMonth) {
      setExpandedGroups(new Set([initialGroup]));
      setExpandedMonths(new Set([initialMonth]));
      return;
    }
    const current = new Date();
    const monthName = MONTH_NAMES[current.getMonth()];
    const yearVal = String(current.getFullYear());
    const fullYear = monthGroups.find((g) => g.name === "Full_Year");
    if (fullYear?.children?.find((m) => m.name === monthName)) {
      setSelGroup("Full_Year");
      setSelMonth(monthName);
      setSelDay({ value: monthName, label: monthName });
      setSelYear({ value: yearVal, label: yearVal });
      setExpandedGroups(new Set(["Full_Year"]));
      setExpandedMonths(new Set([monthName]));
    }
  }, [monthGroups, initialGroup, initialMonth]);

  const toggleGroup = (name) =>
    setExpandedGroups((p) => {
      const n = new Set(p);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });
  const toggleMonth = (name) =>
    setExpandedMonths((p) => {
      const n = new Set(p);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });
  const handleGroupSelect = (group) => {
    setSelGroup(group.name);
    setSelMonth(group.name);
    setSelDay({ value: group.name, label: group.name === "Full_Year" ? "Full Year" : "FY" });
    toggleGroup(group.name);
  };
  const handleMonthSelect = (groupName, month) => {
    setSelGroup(groupName);
    setSelMonth(month.name);
    setSelDay({ value: month.name, label: month.name });
    if (month.children?.length) toggleMonth(month.name);
  };
  const handleDaySelect = (groupName, monthName, day) => {
    setSelGroup(groupName);
    setSelMonth(monthName);
    setSelDay({ value: day.name, label: day.name });
  };

  const isComplete = !!selMonth && !!selYear;
  const previewLabel = isComplete ? `${selDay?.label || selMonth} ${selYear.value}` : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -6, scale: 0.97 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: -6, scale: 0.97 }}
      style={{
        position: "fixed",
        left: "296px",
        top: `${topPos}px`,
        zIndex: 9999,
        width: "280px",
        maxHeight: "85vh",
      }}
      className="rounded-xl border border-border bg-card overflow-hidden shadow-2xl"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/40">
        <div className="flex items-center gap-2">
          <CalendarRange className="w-4 h-4 text-primary" />
          <span className="text-xs font-bold text-foreground">Date Filter</span>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading && (
          <div className="flex justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
          </div>
        )}
        {!loading && (
          <>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 font-semibold">
                Year
              </p>
              <ReactSelect
                options={yearOptions}
                value={selYear}
                onChange={setSelYear}
                placeholder="Select year..."
              />
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 font-semibold">
                Month / Period
              </p>
              <div className="border border-border rounded-lg overflow-hidden bg-background max-h-52 overflow-y-auto">
                {monthGroups.map((group) => {
                  const isExpanded = expandedGroups.has(group.name);
                  const isSelected = selGroup === group.name && selMonth === group.name;
                  return (
                    <div key={group.name}>
                      <button
                        onClick={() => handleGroupSelect(group)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left border-b border-border ${isSelected ? "bg-primary/10" : "hover:bg-muted/50"}`}
                      >
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleGroup(group.name);
                          }}
                          className="text-muted-foreground"
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                        </span>
                        <span className="text-xs font-semibold flex-1 text-left">
                          {group.name === "Full_Year" ? "Full Year" : "Fiscal Year"}
                        </span>
                        {isSelected && <Check className="w-3 h-3 text-primary" />}
                      </button>
                      {isExpanded &&
                        group.children?.map((month) => {
                          const isMonthExpanded = expandedMonths.has(month.name);
                          const isMonthSelected =
                            selGroup === group.name &&
                            selMonth === month.name &&
                            selDay?.value === month.name;
                          return (
                            <div key={month.name}>
                              <button
                                onClick={() => handleMonthSelect(group.name, month)}
                                className={`w-full flex items-center gap-2 pl-7 pr-3 py-1.5 text-left border-b border-border/50 ${isMonthSelected ? "bg-primary/8 text-primary" : "hover:bg-muted/40"}`}
                              >
                                {month.children?.length ? (
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleMonth(month.name);
                                    }}
                                    className="text-muted-foreground"
                                  >
                                    {isMonthExpanded ? (
                                      <ChevronDown className="w-3 h-3" />
                                    ) : (
                                      <ChevronRight className="w-3 h-3" />
                                    )}
                                  </span>
                                ) : (
                                  <div className="w-3" />
                                )}
                                <span
                                  className={`text-xs flex-1 text-left ${isMonthSelected ? "font-semibold text-primary" : "text-foreground"}`}
                                >
                                  {month.name}
                                </span>
                                {isMonthSelected && <Check className="w-3 h-3 text-primary" />}
                              </button>
                              {isMonthExpanded &&
                                month.children?.map((day) => (
                                  <button
                                    key={day.name}
                                    onClick={() => handleDaySelect(group.name, month.name, day)}
                                    className={`w-full flex items-center gap-2 pl-14 pr-3 py-1.5 text-left border-b border-border/30 ${
                                      selGroup === group.name && selDay?.value === day.name
                                        ? "bg-primary/10 text-primary"
                                        : "hover:bg-muted/50"
                                    }`}
                                  >
                                    <span className="text-[11px] font-mono">{day.name}</span>
                                    {selGroup === group.name && selDay?.value === day.name && (
                                      <Check className="w-3 h-3 text-primary ml-auto" />
                                    )}
                                  </button>
                                ))}
                            </div>
                          );
                        })}
                    </div>
                  );
                })}
              </div>
            </div>
            {previewLabel && (
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
                <Check className="w-3 h-3 text-primary" />
                <span className="text-[10px] text-primary font-semibold">{previewLabel}</span>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSelGroup("");
                  setSelMonth("");
                  setSelDay(null);
                  onReset();
                }}
                className="flex-1 py-1.5 text-xs font-semibold rounded-lg border border-border text-muted-foreground hover:bg-secondary"
              >
                Reset
              </button>
              <button
                disabled={!isComplete}
                onClick={() =>
                  onApply({
                    month: selDay.value,
                    year: selYear.value,
                    type: selGroup,
                    displayMonth: selDay?.label || selMonth,
                    panelMonth: selMonth,
                  })
                }
                className={`flex-1 py-1.5 text-xs font-semibold rounded-lg ${isComplete ? "bg-primary text-white hover:bg-primary/90" : "bg-muted text-muted-foreground cursor-not-allowed"}`}
              >
                Apply
              </button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

const useScaleDown = (containerRef, baseWidth = 1440) => {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const calc = () => {
      const w = el.clientWidth;
      setScale(w > 0 && w < baseWidth ? w / baseWidth : 1);
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, baseWidth]);
  return scale;
};

// ============================================
// Main Component
// ============================================
const OnStreetEnforcementPage = ({
  selectedCityName,
  selectedDateFilter,
  onContractChange,
  onFiltersChangeExternal,
  isActive = true,
  monthGroups = [],
  yearOptions = [],
  optLoading = false,
  optError = null,
}) => {
  const scaleContainerRef = useRef(null);
  const scale = useScaleDown(scaleContainerRef, 1440);

  const { userContracts } = usePermissions();

  // ============================================
  // CONTRACT OPTIONS - COMPLETE FIX with UNIQUE KEY
  // ============================================
  const contractOptions = useMemo(() => {
    if (!userContracts) return ALL_CONTRACT_OPTIONS;

    // Normalize permissions from API
    const normalized = normalizePermissions(userContracts);

    // 👇 IMPORTANT: Unique keys using code|name to avoid duplicate code issue
    const allowedKeys = new Set([
      ...normalized.onstreet.map((c) => getContractKey(c)),
      ...normalized.offstreet.map((c) => getContractKey(c)),
    ]);

    if (allowedKeys.size === 0) return ALL_CONTRACT_OPTIONS;

    return ALL_CONTRACT_OPTIONS.filter((contract) => {
      const contractKey = getContractKey({ name: contract.value, fullName: contract.label });
      return allowedKeys.has(contractKey);
    });
  }, [userContracts]);

  // ============================================
  // PARENT CONTRACT OPTION - COMPLETE FIX
  // ============================================
  const parentContractOption = useMemo(() => {
    if (!userContracts) return PARENT_CONTRACT_OPTION;

    const normalized = normalizePermissions(userContracts);
    const allowedKeys = new Set([
      ...normalized.onstreet.map((c) => getContractKey(c)),
      ...normalized.offstreet.map((c) => getContractKey(c)),
    ]);

    const parentKey = getContractKey({ name: "901", fullName: "Saudi Arabia" });
    const hasSaudi = allowedKeys.has(parentKey);

    if (hasSaudi || allowedKeys.size === 0) {
      return PARENT_CONTRACT_OPTION;
    }
    return null;
  }, [userContracts]);

  // ============================================
  // State
  // ============================================
  const [selContract, setSelContract] = useState(parentContractOption);
  const [contractSearch, setContractSearch] = useState("");
  const [contractsExpanded, setContractsExpanded] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeFilters, setActiveFilters] = useState(
    selectedDateFilter?.month && selectedDateFilter?.year ? selectedDateFilter : {}
  );
  const [showFilters, setShowFilters] = useState(false);
  const [filterPanelTop, setFilterPanelTop] = useState(12);
  const filterBtnRef = useRef(null);
  const [cnsCardsExpanded, setCnsCardsExpanded] = useState(true);
  const [cardsExpanded, setCardsExpanded] = useState(true);
  const [revenueSummaryTab, setRevenueSummaryTab] = useState("stack");
  const [revenueYearFilter, setRevenueYearFilter] = useState("All");
  const [cnsYearFilter, setCnsYearFilter] = useState("All");
  const setYearFilter = (year) => {
    setRevenueYearFilter(year);
    setCnsYearFilter(year);
  };
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartDataSource, setChartDataSource] = useState("cns");
  const [chartMode, setChartMode] = useState("paid_count");

  const hasActiveFilter = !!(activeFilters?.month && activeFilters?.year);
  const now = new Date();
  const displayMonth =
    activeFilters.displayMonth || activeFilters.month || MONTH_NAMES[now.getMonth()];
  const displayYear = activeFilters.year || String(now.getFullYear());
  const apiMonth = activeFilters.month || MONTH_NAMES[now.getMonth()];

  // Auto-select contract from Parking tab
  useEffect(() => {
    if (!selectedCityName) return;
    const target = normalizeName(selectedCityName);

    if (parentContractOption && normalizeName(parentContractOption.label) === target) {
      setSelContract(parentContractOption);
      setContractsExpanded(true);
      return;
    }

    const matched = contractOptions.find((c) => normalizeName(c.label) === target);
    if (matched) {
      setSelContract(matched);
      setContractsExpanded(true);
    }
  }, [selectedCityName, contractOptions, parentContractOption]);

  useEffect(() => {
    if (selectedDateFilter?.month && selectedDateFilter?.year) setActiveFilters(selectedDateFilter);
  }, [selectedDateFilter]);

  useEffect(() => {
    if (showFilters && filterBtnRef.current) {
      const rect = filterBtnRef.current.getBoundingClientRect();
      setFilterPanelTop(Math.min(Math.max(rect.top, 12), window.innerHeight - 360 - 12));
    }
  }, [showFilters]);

  // ─── Fetch Data ───────────────────────────────────────────────────────────
  const fetchEnforcement = useCallback(async () => {
    if (!selContract?.value) return;
    setLoading(true);
    setError(null);
    try {
      const url = `/get-enforcement-data-os?contract=${selContract.value}&year=${displayYear}&month=${buildMonthParam(apiMonth)}`;
      const response = await apiCall("get", url, null, null, false, false);

      if (response?.success) {
        const d = response.data;
        const hasEnforcementData =
          d?.enforcement_cards && Object.keys(d.enforcement_cards).length > 0;
        const hasKpiData =
          d?.enforcement_kpi_summary && Object.keys(d.enforcement_kpi_summary).length > 0;

        if (!hasEnforcementData && !hasKpiData) {
          setError("No Enforcement Data found for this contract in the selected period.");
          setResult(null);
          return;
        }
        setResult(response);
      } else {
        setError(response?.message || "No data found");
      }
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [selContract, apiMonth, displayYear]);

  useEffect(() => {
    if (isActive) fetchEnforcement();
  }, [fetchEnforcement, isActive]);

  const kpiSummary = result?.data?.enforcement_kpi_summary || null;
  const rows = kpiSummary ? Object.entries(kpiSummary) : [];

  const filteredContracts = useMemo(() => {
    const q = contractSearch.trim().toLowerCase();
    const baseList = contractOptions;
    return q
      ? baseList.filter(
          (c) => c.value.toLowerCase().includes(q) || c.label.toLowerCase().includes(q)
        )
      : baseList;
  }, [contractSearch, contractOptions]);

  const cnsCardsRaw = result?.data?.cns_cards || null;
  const enfCardsRaw = result?.data?.enforcement_cards || null;

  const CARD_FIELDS = [
    { key: "jobs_issue_count", label: "Issued CNS", icon: FileText, tone: "default" },
    { key: "jobs_cancelled", label: "CNS Cancelled", icon: XCircle, tone: "danger" },
    { key: "net_count", label: "Net CNS", icon: CheckCircle2, tone: "success" },
    { key: "jobs_paid_count", label: "Paid CNS", icon: CreditCard, tone: "default" },
    { key: "jobs_paid_rate", label: "Paid Rate", icon: Percent, tone: "default" },
    { key: "revenue", label: "Revenue", icon: Coins, tone: "warning" },
    { key: "eos_employed", label: "EOs Employed", icon: Users, tone: "default" },
    { key: "eos_deployed", label: "EOs Deployed", icon: Users, tone: "default" },
    { key: "percent_deployed_to_employed", label: "Deployed%", icon: TrendingUp, tone: "default" },
    { key: "average_jobs_per_day", label: "Avg CNS Per Day", icon: CalendarRange, tone: "default" },
    {
      key: "average_jobs_per_officer_per_day",
      label: "Avg CNS/Officer Per Day",
      icon: Activity,
      tone: "default",
    },
    { key: "parking_bays", label: "Parking Bays", icon: CircleParking, tone: "default" },
  ];

  const buildCards = (raw, overrides = {}, exclude = []) => {
    if (!raw) return null;
    return CARD_FIELDS.filter((f) => raw[f.key] !== undefined && !exclude.includes(f.key)).map(
      (f) => ({ ...f, label: overrides[f.key] || f.label, value: raw[f.key] ?? "-" })
    );
  };

  const ENF_OVERRIDES = {
    jobs_issue_count: "Issue Jobs",
    net_count: "Net Jobs",
    jobs_paid_count: "Paid Jobs",
    jobs_paid_rate: "Paid Rate",
    jobs_cancelled: "Cancelled Jobs",
    revenue: "Revenue",
    eos_employed: "Drivers Employed",
    eos_deployed: "Drivers Deployed",
    percent_deployed_to_employed: "Deployed%",
    average_jobs_per_day: "Avg Jobs/Day",
    average_jobs_per_officer_per_day: "Avg Jobs/Driver Per Day",
  };

  const cnsCards = buildCards(cnsCardsRaw, {}, ["parking_bays"]);
  const enfCards = buildCards(enfCardsRaw, ENF_OVERRIDES);

  const revenueSummaryRaw = result?.data?.revenue_summary;

  const revenueSummaryPercentChart = useMemo(
    () =>
      buildRevenueSummaryChart(
        revenueSummaryRaw,
        displayMonth,
        displayYear,
        revenueYearFilter,
        "percentage"
      ),
    [revenueSummaryRaw, displayMonth, displayYear, revenueYearFilter]
  );
  const revenueYears = useMemo(
    () => Object.keys(revenueSummaryRaw?.[0]?.data || {}).sort(),
    [revenueSummaryRaw]
  );
  const revenueYearPill = (
    <div className="flex items-center gap-1 bg-card rounded-md p-0.5">
      <button
        onClick={() => setYearFilter("All")}
        className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${revenueYearFilter === "All" ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
      >
        All
      </button>
      {revenueYears.map((year) => (
        <button
          key={year}
          onClick={() => setYearFilter(year)}
          className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${revenueYearFilter === year ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
        >
          {year}
        </button>
      ))}
    </div>
  );
  const revenueSummaryChart = useMemo(
    () => buildRevenueSummaryChart(revenueSummaryRaw, displayMonth, displayYear, revenueYearFilter),
    [revenueSummaryRaw, displayMonth, displayYear, revenueYearFilter]
  );

  const graphData = result?.data?.graph_data;
  const chartData = useMemo(
    () => transformGraphData(graphData, displayMonth, displayYear, chartDataSource, cnsYearFilter),
    [graphData, displayMonth, displayYear, chartDataSource, cnsYearFilter]
  );
  const breakdownKeys = useMemo(
    () => Object.keys(graphData?.[chartDataSource]?.breakdown || {}),
    [graphData, chartDataSource]
  );
  const breakdownKPIData = useMemo(
    () =>
      transformBreakdownToKPI(graphData, chartDataSource, displayMonth, displayYear, cnsYearFilter),
    [graphData, chartDataSource, displayMonth, displayYear, cnsYearFilter]
  );
  const cnsYears = useMemo(() => {
    const src = graphData?.[chartDataSource];
    if (!src) return [];
    const base =
      chartMode === "paid_count"
        ? src.cn_issue_count || {}
        : src.breakdown?.[breakdownKeys[0]] || {};
    return Object.keys(base).sort();
  }, [graphData, chartDataSource, chartMode, breakdownKeys]);
  const cnsYearPill = (
    <div className="flex items-center gap-1 bg-card rounded-md p-0.5">
      <button
        onClick={() => setYearFilter("All")}
        className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${cnsYearFilter === "All" ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
      >
        All
      </button>
      {cnsYears.map((year) => (
        <button
          key={year}
          onClick={() => setYearFilter(year)}
          className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${cnsYearFilter === year ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
        >
          {year}
        </button>
      ))}
    </div>
  );

  const showOverview = selContract && !loading && !error && (rows.length > 0 || cnsCards);
  const highlight = (text, query) => {
    if (!query || !text) return String(text);
    const idx = String(text).toLowerCase().indexOf(query.toLowerCase());
    return idx === -1 ? (
      text
    ) : (
      <>
        {text.slice(0, idx)}
        <mark style={{ background: "#6366F133", padding: "0 1px" }}>
          {text.slice(idx, idx + query.length)}
        </mark>
        {text.slice(idx + query.length)}
      </>
    );
  };

  return (
    <div ref={scaleContainerRef} style={{ width: "100%", height: "100%", overflow: "hidden" }}>
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          width: scale < 1 ? `${(100 / scale).toFixed(2)}%` : "100%",
          height: scale < 1 ? `${(100 / scale).toFixed(2)}%` : "100%",
        }}
      >
        <div className="h-full w-full overflow-hidden bg-background flex gap-4 px-5 pb-5">
          {/* ─── Sidebar ─── */}
          {sidebarOpen && (
            <div className="w-72 flex-shrink-0 h-full flex flex-col border-r bg-card overflow-hidden">
              <div className="flex items-center gap-2.5 py-4 px-1 pr-2 border-b border-border">
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-sm font-bold text-foreground truncate">
                    On Street Enforcement
                  </h1>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest">
                    Contract Explorer
                  </p>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              </div>

              <div className="px-3 py-2.5 border-b border-border">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search contract..."
                      value={contractSearch}
                      onChange={(e) => setContractSearch(e.target.value)}
                      className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    {contractSearch && (
                      <button
                        onClick={() => setContractSearch("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <button
                    ref={filterBtnRef}
                    onClick={() => setShowFilters((v) => !v)}
                    className={`relative p-1.5 rounded-lg border transition-all ${hasActiveFilter ? "bg-primary/15 border-primary/40 text-primary" : "bg-background border-border text-muted-foreground hover:bg-secondary"}`}
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5" />
                    {hasActiveFilter && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-primary" />
                    )}
                  </button>
                </div>
                {hasActiveFilter ? (
                  <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-lg bg-primary/8 border border-primary/15">
                    <Calendar className="w-3 h-3 text-primary" />
                    <span className="text-[9px] text-primary font-semibold truncate">
                      {activeFilters.displayMonth || activeFilters.month} {activeFilters.year}
                    </span>
                    <button
                      onClick={() => {
                        setActiveFilters({});
                        onFiltersChangeExternal?.({});
                      }}
                      className="ml-auto text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ) : (
                  <p className="text-[9px] text-muted-foreground mt-1.5">
                    Showing {displayMonth} {displayYear} (default)
                  </p>
                )}
              </div>

              <AnimatePresence>
                {showFilters && (
                  <DateFilterPanel
                    topPos={filterPanelTop}
                    activeYear={activeFilters?.year}
                    initialGroup={activeFilters?.type || ""}
                    initialMonth={
                      activeFilters?.panelMonth ||
                      activeFilters?.displayMonth ||
                      activeFilters?.month ||
                      ""
                    }
                    initialDay={
                      activeFilters?.month
                        ? { value: activeFilters.month, label: activeFilters.month }
                        : null
                    }
                    onApply={(f) => {
                      setActiveFilters(f);
                      setShowFilters(false);
                      onFiltersChangeExternal?.(f);
                    }}
                    onReset={() => {
                      setActiveFilters({});
                      setShowFilters(false);
                      onFiltersChangeExternal?.({});
                    }}
                    onClose={() => setShowFilters(false)}
                    monthGroups={monthGroups}
                    yearOptions={yearOptions}
                    optLoading={optLoading}
                    optError={optError}
                  />
                )}
              </AnimatePresence>

              <div className="flex-1 overflow-y-auto p-2 space-y-1 pb-10">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1.5 font-semibold">
                  Country &amp; Contracts
                </p>
                <div>
                  {parentContractOption && (
                    <button
                      onClick={() => setContractsExpanded((p) => !p)}
                      className="w-full text-left rounded-lg px-3 py-2.5 bg-primary/5 hover:bg-primary/10 border border-primary/15"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">
                          {contractsExpanded ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                        </span>
                        <Globe className="w-3.5 h-3.5 text-primary" />
                        <div
                          className="flex-1 min-w-0"
                          onClick={() => {
                            const next =
                              selContract?.value === parentContractOption.value
                                ? null
                                : parentContractOption;
                            setSelContract(next);
                            onContractChange?.(next?.label || null);
                          }}
                        >
                          <p
                            className={`text-xs font-bold truncate ${selContract?.value === parentContractOption.value ? "text-primary" : "text-foreground"}`}
                          >
                            {highlight(parentContractOption.label, contractSearch)}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] font-mono text-muted-foreground">
                              Code: {parentContractOption.value}
                            </span>
                            <span className="text-[10px] text-muted-foreground">•</span>
                            <span className="text-[10px] text-muted-foreground">
                              {contractOptions.length} contracts
                            </span>
                          </div>
                        </div>
                        {selContract?.value === parentContractOption.value && (
                          <Check className="w-3.5 h-3.5 text-primary" />
                        )}
                      </div>
                    </button>
                  )}
                  {contractsExpanded && (
                    <div className="ml-4 pl-3 border-l border-border space-y-0.5 py-1">
                      {filteredContracts.map((c) => {
                        const isSelected = selContract?.value === c.value;
                        return (
                          <button
                            key={c.key || c.value}
                            onClick={() => {
                              const next = isSelected ? null : c;
                              setSelContract(next);
                              onContractChange?.(next?.label || null);
                            }}
                            className={`w-full text-left rounded-md px-2.5 py-2 transition-all ${isSelected ? "bg-primary/10 border border-primary/20" : "hover:bg-secondary/40 border border-transparent"}`}
                          >
                            <div className="flex items-center gap-2">
                              <Building2 className="w-3 h-3 text-muted-foreground" />
                              <div className="flex-1 min-w-0">
                                <p
                                  className={`text-[11px] font-semibold truncate ${isSelected ? "text-primary" : "text-foreground"}`}
                                >
                                  {highlight(c.label, contractSearch)}
                                </p>
                                <p className="text-[9px] font-mono text-muted-foreground">
                                  {highlight(c.value, contractSearch)}
                                </p>
                              </div>
                              {isSelected && <Check className="w-3 h-3 text-primary" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ─── Main Content ─── */}
          <div className="flex-1 min-w-0 h-full overflow-y-auto space-y-4 pb-24 mt-8">
            <div className="flex items-center justify-between">
              <div>
                {!sidebarOpen && (
                  <button
                    onClick={() => setSidebarOpen(true)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-secondary text-[10px] font-semibold"
                  >
                    <ChevronRight className="w-3.5 h-3.5" /> Contracts
                  </button>
                )}
              </div>
              {selContract && (
                <p className="text-[10px] text-muted-foreground">
                  Contract:{" "}
                  <span className="text-foreground font-semibold">{selContract.label}</span>
                  {" · "}Year: <span className="text-foreground font-semibold">{displayYear}</span>
                  {" · "}Month:{" "}
                  <span className="text-foreground font-semibold">{displayMonth}</span>
                </p>
              )}
            </div>

            {!selContract && (
              <p className="text-xs text-muted-foreground text-center py-10">
                Select a contract to view data.
              </p>
            )}
            {selContract && loading && <OverviewShimmer tableColumns={ENFORCEMENT_COLUMNS} />}
            {selContract && !loading && error && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                </div>
                <p className="text-sm font-medium text-foreground">No Active Enforcement</p>
                <p className="text-xs text-muted-foreground text-center max-w-sm">
                  {error}. Is contract ke liye selected period mein koi enforcement actions ya
                  deployments available nahi hain.
                </p>
                <button
                  onClick={fetchEnforcement}
                  className="mt-2 px-4 py-2 text-xs font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
                >
                  Try Again / Retry
                </button>
              </div>
            )}

            {showOverview && (
              <div className="space-y-4">
                {/* ─── CNS Cards ─── */}
                {cnsCards && (
                  <div className="rounded-xl border border-border bg-card overflow-hidden">
                    <button
                      onClick={() => setCnsCardsExpanded((v) => !v)}
                      className="w-full flex items-center justify-between px-3.5 py-2.5 hover:bg-secondary/40"
                    >
                      <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                        <CreditCard className="w-3.5 h-3.5 text-primary" /> CNS Overview
                        {cnsCardsRaw?.parking_bays && (
                          <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[9px] font-semibold">
                            <CircleParking className="w-3 h-3" /> {cnsCardsRaw.parking_bays} Bays
                          </span>
                        )}
                      </span>
                      <ChevronDown
                        className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${cnsCardsExpanded ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence>
                      {cnsCardsExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-6 gap-3 p-3.5 pt-0">
                            {cnsCards.map((c) => (
                              <KpiCard
                                key={c.key}
                                icon={c.icon}
                                label={c.label}
                                value={c.value}
                                tone={c.tone}
                              />
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* ─── Enforcement Cards ─── */}
                {enfCards && (
                  <div className="rounded-xl border border-border bg-card overflow-hidden">
                    <button
                      onClick={() => setCardsExpanded((v) => !v)}
                      className="w-full flex items-center justify-between px-3.5 py-2.5 hover:bg-secondary/40"
                    >
                      <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5 text-primary" /> Enforcement Overview
                      </span>
                      <ChevronDown
                        className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${cardsExpanded ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence>
                      {cardsExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-6 gap-3 p-3.5 pt-0">
                            {enfCards.map((c) => (
                              <KpiCard
                                key={c.key}
                                icon={c.icon}
                                label={c.label}
                                value={c.value}
                                tone={c.tone}
                              />
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* ─── Row 1: Revenue Summary ─── */}
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                  <div className="flex items-center justify-end px-3.5 py-2.5 border-b border-border gap-2">
                    <div className="flex items-center gap-1 bg-secondary/60 rounded-md p-0.5">
                      <button
                        onClick={() => setRevenueSummaryTab("stack")}
                        className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${revenueSummaryTab === "stack" ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                      >
                        Stack
                      </button>
                      <button
                        onClick={() => setRevenueSummaryTab("line")}
                        className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${revenueSummaryTab === "line" ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                      >
                        Line
                      </button>
                    </div>
                  </div>

                  {revenueSummaryChart.datasets.length > 0 ? (
                    revenueSummaryTab === "line" ? (
                      <LineBar
                        title="Revenue Summary"
                        subtitle={`${selContract?.label} • ${revenueYearFilter === "All" ? `${displayMonth} - ${displayYear}` : revenueYearFilter}`}
                        months={revenueSummaryChart.months}
                        yearRanges={revenueSummaryChart.yearRanges}
                        datasets={revenueSummaryChart.datasets}
                        yAxisTitle="Revenue (LC)"
                        topRightContent={revenueYearPill}
                      />
                    ) : revenueSummaryTab === "stack" ? (
                      <StackedBarChart
                        title="Revenue Summary"
                        subtitle={`${selContract?.label} • ${revenueYearFilter === "All" ? `${displayMonth} - ${displayYear}` : revenueYearFilter}`}
                        months={revenueSummaryChart.months}
                        yearRanges={revenueSummaryChart.yearRanges}
                        datasets={revenueSummaryChart.datasets}
                        yAxisTitle="Revenue (LC)"
                        topRightContent={revenueYearPill}
                      />
                    ) : (
                      <StackedAreaPercentChart
                        title="Revenue Summary"
                        subtitle={`${selContract?.label} • ${revenueYearFilter === "All" ? `${displayMonth} - ${displayYear}` : revenueYearFilter} `}
                        yearRanges={revenueSummaryChart.yearRanges}
                        months={revenueSummaryPercentChart.months}
                        datasets={revenueSummaryPercentChart.datasets}
                        yAxisTitle="Revenue %"
                        topRightContent={revenueYearPill}
                      />
                    )
                  ) : (
                    <p className="text-[11px] text-muted-foreground text-center py-10">
                      No revenue data available.
                    </p>
                  )}
                </div>

                {/* ─── Row 2: Chart (Full width) ─── */}
                {(chartMode === "paid_count"
                  ? chartData.length > 0
                  : breakdownKPIData.length > 0) && (
                  <div className="rounded-xl border border-border bg-card overflow-hidden">
                    <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-border gap-2">
                      <p className="text-xs font-semibold text-foreground"></p>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 bg-secondary/60 rounded-md p-0.5">
                          <button
                            onClick={() => setChartDataSource("cns")}
                            className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${chartDataSource === "cns" ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                          >
                            CNS
                          </button>
                          <button
                            onClick={() => setChartDataSource("enforcement")}
                            className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${chartDataSource === "enforcement" ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                          >
                            Enforcement
                          </button>
                        </div>
                        <Select value={chartMode} onValueChange={setChartMode}>
                          <SelectTrigger
                            size="sm"
                            className="h-7 w-auto text-[10px] font-semibold bg-secondary/60"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="paid_count">Quantitative</SelectItem>
                            <SelectItem value="breakdown">Breakdown</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {chartMode === "paid_count" ? (
                      <NewChartKPI
                        chartData={chartData}
                        title="On Street CNS And Enforcement"
                        subtitle={`${selContract?.label} • ${displayMonth} ${displayYear}`}
                        colors={{
                          revenue: "#1094C4",
                          transaction: "#D67432",
                          occupancy: "#E7E708",
                        }}
                        labels={{
                          revenue: chartDataSource === "cns" ? "CNS Issued" : "Enforcement Issued",
                          transaction: chartDataSource === "cns" ? "CNS Paid" : "Enforcement Paid",
                          occupancy: "Paid Rate",
                        }}
                        showDataLabels={true}
                        showRamadanRow={false}
                        yAxisLabel="Count"
                        bottomLabel="Months"
                        topRightContent={cnsYearPill}
                      />
                    ) : (
                      <NewChartKPI
                        chartData={breakdownKPIData}
                        title="On Street CNS And Enforcement"
                        subtitle={`${selContract?.label} • Breakdown • ${displayMonth} ${displayYear}`}
                        colors={{
                          revenue: breakdownColorFor(breakdownKeys[0], 0),
                          transaction: breakdownColorFor(breakdownKeys[1], 1),
                          occupancy: breakdownColorFor(breakdownKeys[2], 2),
                        }}
                        labels={{
                          revenue: breakdownLabelFor(breakdownKeys[0]),
                          transaction: breakdownLabelFor(breakdownKeys[1]),
                          occupancy: breakdownLabelFor(breakdownKeys[2]),
                        }}
                        showRightAxisValues={false}
                        yAxisFormatters={BREAKDOWN_Y_AXIS_FORMATTERS}
                        showDataLabels={true}
                        showRamadanRow={false}
                        yAxisLabel="Count"
                        bottomLabel="Months"
                        topRightContent={cnsYearPill}
                      />
                    )}
                  </div>
                )}

                {/* ─── KPI Summary Table ─── */}
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                  <p className="text-xs font-semibold text-foreground px-3.5 py-2.5 border-b border-border">
                    Enforcement KPI Summary
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-secondary/60 border-b border-border">
                          <th className="text-left px-3.5 py-2.5 text-[9px] uppercase tracking-widest text-muted-foreground font-semibold whitespace-nowrap">
                            Measures
                          </th>
                          {ENFORCEMENT_COLUMNS.map((col) => (
                            <th
                              key={col}
                              className="text-right px-3 py-2.5 text-[9px] uppercase tracking-widest text-muted-foreground font-semibold whitespace-nowrap"
                            >
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(([label, values], idx) => (
                          <tr
                            key={label}
                            className={`border-b border-border/50 last:border-b-0 ${idx % 2 === 1 ? "bg-secondary/20" : ""}`}
                          >
                            <td className="px-3.5 py-2.5 text-[11px] font-medium whitespace-nowrap">
                              {label}
                            </td>
                            {ENFORCEMENT_COLUMNS.map((col) => (
                              <td
                                key={col}
                                className={`text-right px-3 py-2.5 text-[11px] tabular-nums whitespace-nowrap ${isNegative(values[col]) ? "text-red-500 font-semibold" : ""}`}
                              >
                                {values[col] ?? "-"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnStreetEnforcementPage;
